import type { Worker as TesseractWorker } from 'tesseract.js';
import { VALUE_SCAN_PURPOSES, type ValueScanPurpose } from '@/shared/camera/value-scan-purposes';

export interface OcrScannerResult {
  confidence: number;
  text: string;
}

export interface OcrScanOptions {
  purpose?: ValueScanPurpose;
  segmentation?: 'block' | 'sparse';
}

// Served from our own origin and precached by the service worker so OCR works offline.
// Keep in sync with vite.config.mts and src/public/service-worker.js.
const TESSERACT_ASSET_PATH = '/tesseract';

// Restricting the recognized alphabet per purpose is one of the biggest accuracy levers
// for Tesseract: it stops meter-glass glare and nameplate pictograms from turning into
// stray punctuation and letters.
const CHAR_WHITELISTS: Partial<Record<ValueScanPurpose, string>> = {
  [VALUE_SCAN_PURPOSES.devEui]: '0123456789ABCDEFabcdef:-',
  [VALUE_SCAN_PURPOSES.registerCounter]: '0123456789.,',
  [VALUE_SCAN_PURPOSES.registerObis]: '0123456789.-',
  [VALUE_SCAN_PURPOSES.registerUnit]: 'kWhMGJm3',
  [VALUE_SCAN_PURPOSES.serial]: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789./\\:-',
};

let workerPromise: Promise<TesseractWorker> | null = null;

function getSharedTesseractWorker() {
  workerPromise ??= createTesseractWorker().catch((error: unknown) => {
    workerPromise = null;
    throw error;
  });

  return workerPromise;
}

async function createTesseractWorker() {
  const { createWorker } = await import('tesseract.js');

  return createWorker('eng', undefined, {
    corePath: TESSERACT_ASSET_PATH,
    langPath: TESSERACT_ASSET_PATH,
    workerPath: `${TESSERACT_ASSET_PATH}/worker.min.js`,
  });
}

export class TesseractOcrScannerAdapter {
  readonly id = 'tesseract';

  isSupported() {
    return typeof HTMLCanvasElement !== 'undefined';
  }

  async scan(source: HTMLCanvasElement, options: OcrScanOptions = {}): Promise<OcrScannerResult[]> {
    const [worker, { PSM }] = await Promise.all([getSharedTesseractWorker(), import('tesseract.js')]);

    await worker.setParameters({
      tessedit_char_whitelist: (options.purpose && CHAR_WHITELISTS[options.purpose]) ?? '',
      tessedit_pageseg_mode: options.segmentation === 'block' ? PSM.SINGLE_BLOCK : PSM.SPARSE_TEXT,
    });

    const result = await worker.recognize(source);
    const text = result.data.text?.trim() ?? '';

    if (!text) {
      return [];
    }

    return [
      {
        // Tesseract reports confidence on a 0-100 scale; normalize to 0-1 here so
        // downstream scoring never has to guess the scale.
        confidence: Math.min(1, Math.max(0, (result.data.confidence ?? 0) / 100)),
        text,
      },
    ];
  }
}
